import { API_CONFIG } from '@Web/config/api.config';
import { RestClient as RestClientImpl } from '@Web/clients/RestClient';
import { AuthService } from '@Web/services/AuthService';

export type PeriodFilter = 'day' | 'month' | 'year';

export interface LeaderboardEntry {
  nickname?: string;
  date: string;
  currency?: string;
  bet?: number;
  win?: number;
  resultMultiplier?: number;
  roundMaxMultiplier?: number;
  multiplier?: number;
  roundNo?: number;
  tableNo?: number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
}

export interface PreviousRoundPlayer {
  nickname: string;
  bet: number;
  cashoutMul: number | null;
  cashoutAmount: number;
}

export interface PreviousRoundResponse {
  roundId: string;
  crashMultiplier: number;
  players: PreviousRoundPlayer[];
}

export class LeaderboardService {
  private static instance: LeaderboardService;
  private restClient: RestClientImpl;

  private constructor() {
    this.restClient = new RestClientImpl(API_CONFIG.baseUrl);
  }

  public static getInstance(): LeaderboardService {
    if (!LeaderboardService.instance) {
      LeaderboardService.instance = new LeaderboardService();
    }
    return LeaderboardService.instance;
  }

  private getHeaders() {
    const token = AuthService.getInstance().getAccessToken();

    return token
      ? {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      : { 'Content-Type': 'application/json' };
  }

  public async getTopMultiplier(
    period: PeriodFilter,
    gameCode: number,
    tableNo: number
  ) {
    try {
      const url = `${API_CONFIG.endpoints.leaderboard.multiplier}?period=${period}&gameCode=${gameCode}&tableNo=${tableNo}`;
      return (
        await this.restClient.get<LeaderboardResponse>(url, {
          headers: this.getHeaders(),
        })
      ).entries;
    } catch (e) {
      console.error('Failed to fetch Top Multiplier', e);
      return [];
    }
  }

  public async getTopWin(
    period: PeriodFilter,
    gameCode: number,
    tableNo: number
  ) {
    try {
      const url = `${API_CONFIG.endpoints.leaderboard.win}?period=${period}&gameCode=${gameCode}&tableNo=${tableNo}`;
      return (
        await this.restClient.get<LeaderboardResponse>(url, {
          headers: this.getHeaders(),
        })
      ).entries;
    } catch (e) {
      console.error('Failed to fetch Top Win', e);
      return [];
    }
  }

  public async getTopRounds(
    period: PeriodFilter,
    gameCode: number,
    tableNo: number
  ) {
    try {
      const url = `${API_CONFIG.endpoints.leaderboard.rounds}?period=${period}&gameCode=${gameCode}&tableNo=${tableNo}`;
      return (
        await this.restClient.get<LeaderboardResponse>(url, {
          headers: this.getHeaders(),
        })
      ).entries;
    } catch (e) {
      console.error('Failed to fetch Top Rounds', e);
      return [];
    }
  }

  /** NEW: fetch previous round */
  public async getPreviousRound(
    gameCode: number,
    tableNo: number
  ): Promise<PreviousRoundResponse | null> {
    try {
      const url = `${API_CONFIG.endpoints.previousRound}?gameCode=${gameCode}&tableNo=${tableNo}`;
      return await this.restClient.get<PreviousRoundResponse>(url, {
        headers: this.getHeaders(),
      });
    } catch (error) {
      console.error('Failed to fetch previous round:', error);
      return null;
    }
  }
}
